import { useMemo, useEffect } from "react";
import {
  usePreferencesStore,
  type BrandPreference,
} from "../store/preferences-store";
import { usePreferenceBrands } from "./use-preference-brands";
import type { PreferenceBrand } from "../services/typesense-preference-brands";
import { useCategoryPreferences } from "@/api/hooks/use-category-preferences";
import { useUserPreferences } from "@/api/hooks/use-user-preferences";
import { useUpdatePreferences } from "@/api/hooks/use-update-preferences";
import type { CategoryPreferenceAttribute } from "@/api/models/api-types";
import { useToast } from "@/features/toast/hooks/use-toast";
import { useTranslation } from "react-i18next";

export const usePreferences = () => {
  const { t } = useTranslation();
  const { success: successToast, error: errorToast } = useToast();
  const store = usePreferencesStore();

  // Fetch categories from backend (type-based)
  const {
    data: categoriesResponse,
    isLoading: isCategoriesLoading,
    error: categoriesError,
  } = useCategoryPreferences("TR");

  // Fetch user preferences from backend
  const {
    data: userPreferencesResponse,
    isLoading: isUserPreferencesLoading,
    error: userPreferencesError,
  } = useUserPreferences();

  // Fetch brands from Typesense
  const { data: brandsData, isLoading: isBrandsLoading } =
    usePreferenceBrands(200);

  // Update preferences mutation
  const {
    mutate: updatePreferences,
    isPending: isSaving,
    error: saveError,
  } = useUpdatePreferences();

  // Categories from backend
  const categories = useMemo(
    () => categoriesResponse?.data || [],
    [categoriesResponse]
  );

  // User preferences from backend
  const userPreferences = useMemo(
    () => {
      const data = userPreferencesResponse?.data;
      if (!data) return null;
      return {
        ...data,
        preferences: data.preferences ?? {},
        brandPreferences: data.brandPreferences ?? [],
      };
    },
    [userPreferencesResponse]
  );

  // Brands from Typesense
  const brands: PreferenceBrand[] = useMemo(
    () => brandsData || [],
    [brandsData]
  );

  // Sync user preferences from backend to store on load (only once when data arrives)
  useEffect(() => {
    if (userPreferences) {
      const safePreferences = userPreferences.preferences ?? {};
      const safeBrandPreferences = userPreferences.brandPreferences ?? [];

      // Sync size preferences (type-based)
      Object.entries(safePreferences).forEach(([type, sizes]) => {
        if (sizes && sizes.length > 0) {
          store.setTypeSizes(type, sizes);
        }
      });

      // Sync brand preferences
      if (safeBrandPreferences.length > 0) {
        store.setBrandPreferences(safeBrandPreferences);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPreferencesResponse]);

  // Get selected sizes for a type
  const getTypeSizes = (type: string): CategoryPreferenceAttribute[] => {
    return store.sizePreferences[type] || [];
  };

  // Check if a size is selected for a type
  const isSizeSelected = (type: string, attributeValueId: string): boolean => {
    return getTypeSizes(type).some(
      (s) => s.attributeValueId === attributeValueId
    );
  };

  // Toggle size selection for a type
  const toggleTypeSize = (type: string, size: CategoryPreferenceAttribute) => {
    if (isSizeSelected(type, size.attributeValueId)) {
      store.removeTypeSize(type, size.attributeValueId);
    } else {
      store.addTypeSize(type, size);
    }
  };

  // Check if a brand is favorite
  const isBrandFavorite = (brandId: string): boolean => {
    return store.brandPreferences.some((b) => b.id === brandId);
  };

  // Toggle favorite brand
  const toggleBrand = (brand: BrandPreference) => {
    if (isBrandFavorite(brand.id)) {
      store.removeBrandPreference(brand.id);
    } else {
      store.addBrandPreference(brand);
    }
  };

  // Save preferences to backend
  const savePreferences = async () => {
    try {
      await new Promise((resolve) => {
        updatePreferences(
          {
            preferences: store.sizePreferences,
            brandPreferences: store.brandPreferences,
          },
          {
            onSuccess: () => {
              successToast(
                t("preferences.saveSuccess") || "Preferences saved successfully"
              );
              resolve(true);
            },
            onError: (saveErrorResponse) => {
              errorToast(
                t("preferences.saveError") || "Failed to save preferences"
              );
              console.error("Save preferences error:", saveErrorResponse);
              resolve(false);
            },
          }
        );
      });
    } catch (saveErrorCatch) {
      console.error("Save preferences error:", saveErrorCatch);
      errorToast(t("preferences.saveError") || "Failed to save preferences");
    }
  };

  return {
    // Categories from backend (type-based)
    categories,
    isCategoriesLoading,
    categoriesError,

    // User preferences from backend
    userPreferences,
    isUserPreferencesLoading,
    userPreferencesError,

    // Brands from Typesense
    brands,
    isBrandsLoading,

    // Local state
    sizePreferences: store.sizePreferences,
    brandPreferences: store.brandPreferences,

    // Size Preferences Actions (Type-based)
    getTypeSizes,
    isSizeSelected,
    toggleTypeSize,
    setTypeSizes: store.setTypeSizes,
    clearTypeSizes: store.clearTypeSizes,

    // Brand Preferences Actions
    isBrandFavorite,
    toggleBrand,
    setBrandPreferences: store.setBrandPreferences,
    clearBrandPreferences: store.clearBrandPreferences,

    // Save to backend
    savePreferences,
    isSaving,
    saveError,

    // Reset
    reset: store.reset,
  };
};
